import React, { useState, useMemo } from "react";
import {
  Select,
  MenuItem,
  FormControl,
  Checkbox,
  ListItemText,
  ListSubheader,
  OutlinedInput,
  TextField,
  InputAdornment,
  Divider,
} from "@mui/material";
import { Search } from "lucide-react";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  // Keep focus on the search box instead of the first menu item.
  autoFocus: false,
  PaperProps: {
    sx: {
      maxHeight: ITEM_HEIGHT * 5.5 + ITEM_PADDING_TOP,
      width: 300,
      "& .MuiMenuItem-root.Mui-selected": { backgroundColor: "#fdebea" },
      "& .MuiMenuItem-root.Mui-selected:hover": { backgroundColor: "#fbdedc" },
      "& .MuiMenuItem-root:hover": { backgroundColor: "#fff5f4" },
    },
  },
};

const SELECT_ALL = "__select_all__";

export default function EquipmentGroupSelect({
  options = [],
  value = [],
  onChange,
}) {
  const [search, setSearch] = useState("");

  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) =>
      String(opt.label).toLowerCase().includes(q),
    );
  }, [options, search]);

  const filteredValues = filteredOptions.map((o) => o.value);
  const allFilteredSelected =
    filteredValues.length > 0 &&
    filteredValues.every((v) => value.includes(v));
  const someFilteredSelected =
    filteredValues.some((v) => value.includes(v)) && !allFilteredSelected;

  const handleChange = (e) => {
    const selected = e.target.value;

    // "Select all" toggles the currently visible (filtered) options.
    if (selected.includes(SELECT_ALL)) {
      if (allFilteredSelected) {
        onChange(value.filter((v) => !filteredValues.includes(v)));
      } else {
        onChange(Array.from(new Set([...value, ...filteredValues])));
      }
      return;
    }

    onChange(typeof selected === "string" ? selected.split(",") : selected);
  };

  return (
    <FormControl fullWidth size="small">
      <Select
        multiple
        value={value}
        onChange={handleChange}
        displayEmpty
        input={<OutlinedInput />}
        onClose={() => setSearch("")}
        renderValue={(selected) => {
          if (!selected || selected.length === 0) {
            return (
              <span style={{ color: "#9ca3af" }}>
                Select equipment groups...
              </span>
            );
          }
          return selected
            .map(
              (id) => options.find((opt) => opt.value === id)?.label || id,
            )
            .join(", ");
        }}
        MenuProps={MenuProps}
      >
        <ListSubheader sx={{ p: 1, bgcolor: "background.paper" }}>
          <TextField
            size="small"
            autoFocus
            fullWidth
            placeholder="Search equipment groups..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              // Stop the Select's type-ahead from swallowing keystrokes.
              if (e.key !== "Escape") {
                e.stopPropagation();
              }
            }}
            onClick={(e) => e.stopPropagation()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root.Mui-focused": {
                boxShadow: "0 0 0 3px rgba(183, 28, 28, 0.1)",
              },
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#b71c1c",
                borderWidth: "1px",
              },
            }}
          />
        </ListSubheader>

        <MenuItem value={SELECT_ALL} disabled={filteredValues.length === 0}>
          <Checkbox
            checked={allFilteredSelected}
            indeterminate={someFilteredSelected}
            sx={{ color: "#cbd5e1", "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "#b71c1c" } }}
          />
          <ListItemText
            primary={search.trim() ? "Select all (filtered)" : "Select all"}
          />
        </MenuItem>

        <Divider />

        {filteredOptions.length === 0 ? (
          <MenuItem disabled>No equipment groups found</MenuItem>
        ) : (
          filteredOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              <Checkbox
                checked={value.includes(option.value)}
                sx={{ color: "#cbd5e1", "&.Mui-checked": { color: "#b71c1c" } }}
              />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))
        )}
      </Select>
    </FormControl>
  );
}
